import { useQuery, useQueryClient, useMutation } from 'react-query';
import GroupConfigurationAPI from 'services/API/GroupConfigurationAPI';
import type { GROUP_TYPE } from 'utils/constants';

export const useGroupConfigurations = ({
  userId,
  groupType,
  keepPreviousData = true,
}: {
  userId: string | undefined;
  groupType?: GROUP_TYPE;
  keepPreviousData?: boolean;
}) => {
  return useQuery(
    ['groupConfigurations', userId, groupType],
    async () => {
      if (!userId) {
        return null;
      }

      const response = await GroupConfigurationAPI.getGroupsForUser(groupType);

      return response?.data;
    },
    {
      keepPreviousData,
      enabled: !!userId,
      cacheTime: 0,
      refetchOnWindowFocus: false,
    },
  );
};

export const useInvalidateGroupConfigurations = () => {
  const queryClient = useQueryClient();

  return {
    invalidateGroupConfigurations: (userId: string, groupType?: GROUP_TYPE) => {
      // When no groupType is given, invalidate every variant for this user
      // (the untyped list plus each typed list) via prefix matching, so a
      // newly created/edited group is reflected everywhere it's shown.
      queryClient.invalidateQueries(
        groupType === undefined
          ? ['groupConfigurations', userId]
          : ['groupConfigurations', userId, groupType],
      );
    },
  };
};

export const useSaveCompanyToGroupConfig = () => {
  const addCompanyToGroupConfigs = useMutation({
    mutationFn: async ({
      groupIds,
      companyId,
    }: {
      groupIds: number[];
      companyId: number;
    }) => {
      const response = await GroupConfigurationAPI.postSaveCompanyToGroupConfigs({
        groupIds,
        companyId,
      });

      return response?.data;
    },
  });

  return { addCompanyToGroupConfigs };
};
